import React from "react";
import { Input } from "reactstrap";
import { FormFieldConfig } from "Components/Common/CommonForm";
import { Column } from "Components/Common/CommonTableIndexWise";
import styles from "../css/dispensing.module.scss";
import ip from "../../IPinventory/css/ipInventory.module.scss";
import { formatDate } from "common/service/CommonService";

/* ------------------------------------------------------------------------ */
/* FORM SECTION CONFIGURATIONS                                              */
/* ------------------------------------------------------------------------ */

export const getStudyInformationFields = (
  editId: number | null,
  studyOptions: any[],
  periodOptions: any[]
): FormFieldConfig[] => [
    {
      name: "study",
      label: "Study Code",
      type: "select",
      placeholder: "Select study code",
      options: studyOptions,
      required: true,
      disabled: true,
    },
    {
      name: "periodno",
      label: "Period No.",
      type: "select",
      placeholder: "Select Period",
      options: periodOptions,
      required: true,
      disabled : !!editId,
    },
  ];

export const getIpDetailFields = (
  editId: number | null,
  ipCategoryOptions: any[],
  selectedPeriodNo: number | null
): FormFieldConfig[] => [
    {
      name: "ipcategory",
      label: "IP Category",
      type: "radio",
      placeholder: "Select IP category",
      options: ipCategoryOptions,
      disabled: !selectedPeriodNo || !!editId,
      required: true,
    },
    {
      name: "productname",
      label: "Product Name (Dosage Form & Strength)",
      type: "text",
      placeholder: !selectedPeriodNo
        ? "Select Period first"
        : "Select IP category first",
      disabled: true,
    },
    {
      name: "batchlotno",
      label: "Batch / Lot No.",
      type: "text",
      placeholder: !selectedPeriodNo
        ? "Select Period first"
        : "Select IP category first",
      disabled: true,
    },
    {
      name: "manufacturingdate",
      label: "Manufacturing Date",
      type: "text",
      placeholder: !selectedPeriodNo
        ? "Select Period first"
        : "Select IP category first",
      disabled: true,
    },
    {
      name: "expirydate",
      label: "Expiry / Retest Date",
      type: "text",
      placeholder: !selectedPeriodNo
        ? "Select Period first"
        : "Select IP category first",
      disabled: true,
    },
    {
      name: "manufacturerlabel",
      label: "Manufacturer Label",
      type: "text",
      placeholder: !selectedPeriodNo
        ? "Select Period first"
        : "Select IP category first",
      disabled: true,
    },
    {
      name: "manufacturername",
      label: "Manufacturer Name",
      type: "text",
      placeholder: !selectedPeriodNo
        ? "Select Period first"
        : "Select IP category first",
      disabled: true,
    },
  ];

export const getPerSubjectQtyFields = (unittext: string): FormFieldConfig[] => [
  {
    name: "persubjectqty",
    label: `Quantity to Dispense Per Subject (${unittext})`,
    type: "number",
    placeholder: "Enter quantity per subject",
    required: true,
  },
];

export const getDosingQuantityFields = (): FormFieldConfig[] => [
  {
    name: "persubjectqty",
    label: "Per Subject Quantity",
    type: "number",
    placeholder: "Enter quantity per subject",
    disabled: true,
  },
  {
    name: "extraquantity",
    label: "Extra Quantity",
    type: "number",
    placeholder: "Enter extra quantity if needed",
  },
  {
    name: "totalqtyneeded",
    label: "Total Quantity Needed",
    type: "text",
    placeholder: "Auto-calculated total quantity",
    disabled: true,
  },
];

/* ------------------------------------------------------------------------ */
/* TABLE COLUMNS CONFIGURATIONS                                             */
/* ------------------------------------------------------------------------ */

export const getDeductionTableColumns = (
  deductions: any,
  unittext: string,
  handleDeductionQtyChange: (cNum: string, initialQty: number, val: string) => void,
  handleDeductionReasonChange: (cNum: string, reason: string) => void
): Column[] => [
    {
      key: "containernumber",
      label: "Container No.",
      width: "150px",
      render: (_, row: any) => (
        <div className="d-flex align-items-center gap-2">
          <i className="ri-box-1-line text-primary fs-5" />
          <span className="fw-bold text-dark fs-13">
            {row.containernumber}
          </span>
        </div>
      ),
    },
    {
      key: "initialquantity",
      label: "Initial Available Qty",
      width: "170px",
      textCenter: true,
      render: (val: any) => (
        <span className="px-3 py-1 rounded-pill fs-12 fw-bold bg-primary-subtle text-primary border border-primary-subtle">
          {val} {unittext}
        </span>
      ),
    },
    {
      key: "deductedqty",
      label: "Deducted Qty",
      width: "160px",
      render: (_, row: any) => {
        const currentDed = deductions[row.containernumber] || {
          reason: "",
          deductedqty: "",
        };
        return (
          <Input
            type="number"
            step="any"
            bsSize="sm"
            placeholder="0"
            min={0}
            max={row.initialquantity}
            value={currentDed.deductedqty}
            onKeyDown={(e) => {
              if (["-", "+", "e", "E"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) =>
              handleDeductionQtyChange(
                row.containernumber,
                row.initialquantity,
                e.target.value
              )
            }
            className={`rounded-2 border-slate fs-12 ${styles.inputSm}`}
          />
        );
      },
    },
    {
      key: "deductionreason",
      label: "Deduction Reason",
      width: "280px",
      render: (_, row: any) => {
        const currentDed = deductions[row.containernumber] || {
          reason: "",
          deductedqty: "",
        };

        return (
          <Input
            type="text"
            bsSize="sm"
            placeholder="Enter deduction reason"
            value={currentDed.reason}
            disabled={!currentDed.deductedqty}
            onChange={(e) =>
              handleDeductionReasonChange(row.containernumber, e.target.value)
            }
            className={`rounded-2 border-slate fs-12 ${styles.inputReasonSm}`}
          />
        );
      },
    },
    {
      key: "finalqty",
      label: "Final Calculated Qty",
      width: "180px",
      render: (_, row: any) => {
        const currentDed = deductions[row.containernumber] || {
          reason: "",
          deductedqty: 0,
        };
        const dedQtyNum = Number(currentDed.deductedqty) || 0;
        const finalQty = Math.max(0, row.initialquantity - dedQtyNum);

        return (
          <span
            className={`px-3 py-1 rounded-pill fs-12 fw-bold d-inline-flex align-items-center gap-1 ${finalQty > 0
                ? "bg-success-subtle text-success border border-success-subtle"
                : "bg-danger-subtle text-danger border border-danger-subtle"
              }`}
          >
            <i className={finalQty > 0 ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"} />
            {finalQty} {unittext}
          </span>
        );
      },
    },
  ];

export const getDispensingLogListColumns = (
  onNavigateView: (dispensingId: number) => void
): Column[] => [
    {
      key: "logid",
      label: "LOG ID",
      type: "link",
      onClick: (row: any) => onNavigateView(row.dispensingid),
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span className={ip.link}>{row.logid}</span>
          </div>
        );
      },
    },
    {
      key: "studyprotocol",
      label: "STUDY CODE",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.studyprotocol}</p>
            <p className={ip.cellSecondary}>{row.studyname}</p>
          </>
        );
      },
    },
    {
      key: "productname",
      label: "PRODUCT NAME",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.productname}</p>
            <p className={ip.cellSecondary}>{row.ipcategory}</p>
          </>
        );
      },
    },
    {
      key: "batchlotno",
      label: "BATCH / LOT NO",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.batchlotno}</p>
            <p className={ip.cellSecondary}>
              EXP : {formatDate(row.expirydate || 0)}
            </p>
          </>
        );
      },
    },
    {
      key: "subjectrange",
      label: "SUBJECT RANGE",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.subjectrange}</p>
            <p className={ip.cellSecondary}>
              Available : {row.availablequantity || 0}
            </p>
          </>
        );
      },
    },
    {
      key: "quantity",
      label: "QUANTITY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.totalquantity} Units</p>
            <p className={ip.cellSecondary}>{row.qtypersubject} Per Sub</p>
          </>
        );
      },
    },
    {
      key: "verified",
      label: "VERIFIED BY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.verifiedbyname || "—"}</p>
            <p className={ip.cellSecondary}>
              {row.verifieddate ? formatDate(row.verifieddate) : ""}
            </p>
          </>
        );
      },
    },
  ];
